import React, { useRef } from "react";
import { fixLabel } from "util/manualDeliveryForm";
import { sentence } from "util/manualDeliveryStatus";
import { pickupRange } from "util/manualDeliveryTime";
import { formatCents } from "util/manualShipment";

// Shown before the first quotes answer arrives; the own driver never waits on a carrier.
export const OWN_FALLBACK = {
  id: "own",
  kind: "own",
  carrier_id: null,
  provider: "STORE",
  name: "Your own driver",
  status: "ok",
  price_cents: null,
  note: "Store delivery fee. No label; you mark it out for delivery and delivered.",
  extra: null,
  labels: false,
  dashboard_url: null,
  signature_forced: false,
};

function priceText(option, updating) {
  if (updating) return "…";
  return Number.isInteger(option.price_cents) ? formatCents(option.price_cents) : "No price";
}

// A carrier that did not answer (retryable) could not price this delivery; one that refused it cannot take it.
function reasonText(option) {
  const lead = option.retryable ? "Couldn't get a price" : "Can't take this delivery";
  const reason = sentence(option.reason);
  return reason ? `${lead}: ${reason}` : `${lead}.`;
}

// One radio card per option, all priced for the same shipment. Cards come from the server's capability data
// (QuoteAll); a new carrier needs no change here. An unavailable card can still be selected so the footer can say
// why it cannot be booked; its fix button applies the carrier's suggested change without selecting it.
export default function CarrierOptions({ options, fresh, loading, selectedId, onSelect, onFix, groupRef }) {
  const refs = useRef([]);
  const index = Math.max(
    0,
    options.findIndex((o) => o.id === selectedId)
  );
  const move = (to) => {
    const next = (to + options.length) % options.length;
    onSelect(options[next].id);
    if (refs.current[next]) refs.current[next].focus();
  };
  const onKeyDown = (event) => {
    if (event.target.getAttribute("role") !== "radio") return;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();
      move(index + 1);
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();
      move(index - 1);
    } else if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const at = refs.current.indexOf(event.target);
      if (at >= 0) onSelect(options[at].id);
    }
  };
  const updating = !fresh || loading;

  return (
    <div ref={groupRef} role="radiogroup" aria-label="Carrier" className="md-cards" onKeyDown={onKeyDown}>
      {options.map((option, i) => {
        const on = option.id === selectedId;
        const off = option.status !== "ok";
        const pickup = pickupRange(option.details);
        const note = [pickup ? `Picks up ${pickup}.` : "", option.note].filter(Boolean).join(" ");
        const classes = ["md-card"];
        if (on) classes.push("md-card--on");
        if (off) classes.push("md-card--off");
        return (
          <div
            key={option.id}
            ref={(element) => {
              refs.current[i] = element;
            }}
            role="radio"
            aria-checked={on ? "true" : "false"}
            aria-disabled={off ? "true" : undefined}
            tabIndex={i === index ? 0 : -1}
            className={classes.join(" ")}
            onClick={() => onSelect(option.id)}
          >
            <span className="md-card__radio" aria-hidden="true" />
            <div className="md-card__body">
              <div className="md-card__top">
                <span className="md-card__name">{option.name}</span>
                <span className="md-card__price">{priceText(option, updating)}</span>
              </div>
              {note ? <div className="md-card__note">{note}</div> : null}
              {option.extra && !off ? <div className="md-card__extra">{option.extra}</div> : null}
              {off ? (
                <div className="md-card__reason">
                  <span>{reasonText(option)}</span>
                  {option.fix && fixLabel(option.fix) ? (
                    <button
                      type="button"
                      className="md-btn md-btn--sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onFix(option.fix, option.id);
                      }}
                    >
                      {fixLabel(option.fix)}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
